/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import type { MutableRef } from 'preact/hooks';
import type { FocusableElement } from '#utils/PRIVATE_tabbableUtils';
import type { CurrentKeyDetail, Item } from '#UNSAFE_Collection';
import type { FocusGuardCallbackDetail } from '#PRIVATE_FocusTracker';
import type { CollectionData } from '#PRIVATE_TableCommon/Common.types';
import type { Cell } from '../List.types';
import type {
  ActiveCell,
  ActiveCellDetail,
  CurrencyStatus,
  ScrollDetail,
  ViewportInfo
} from '../List';

import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import {
  getElementContainsFunc,
  handleEnterActionableMode,
  handleWrapActionableFocus
} from '#utils/PRIVATE_collectionUtils';
import {
  allTabbableElements,
  getActiveElement,
  getBodyElement,
  isTabbableElement
} from '#utils/PRIVATE_tabbableUtils';
import { getCellType } from '#PRIVATE_TableCommon/utils/DomUtils';
import { getElementFromCell, isAutoFocusCell, logicalCellExtractor } from '../utils/ListDomUtils';
import {
  applyCurrentCellOverride,
  cellComparator,
  determineActiveCell,
  isTransientCell
} from '../utils/ListFocusUtils';
import { applyScrollDetail } from '../utils/ListScrollUtils';

type UseFocusHandlingProps<K, D> = {
  rootRef: RefObject<HTMLDivElement>;
  viewportInfo: ViewportInfo;
  listId: string;
  listData: CollectionData<K, D>[];
  hasMore: boolean;
  selectionExtensionElementRef: MutableRef<HTMLElement | undefined>;
  currentItemOverride?: Item<K>;
  onPersistCurrentItem?: (detail: CurrentKeyDetail<K>) => void;
  isEmbedded: boolean;
  itemEnterKeyFocusBehavior: 'none' | 'focusWithin';
  isDataLoaded: boolean;
  reorderContext?: { currentItemKey: MutableRef<K | undefined> };
  deferredScrollDetail?: ScrollDetail<K>;
  deferredScrollDetailRef: RefObject<ScrollDetail<K> | undefined>;
  updateDeferredScrollDetail: (scrollDetail: ScrollDetail<K>) => void;
};

// constant timeout values for delays following a input gesture until enabling focus rings
const MOUSE_FOCUS_SHIFT_TIMEOUT = 100;
const TOUCH_FOCUS_SHIFT_TIMEOUT = 200;

// constant event.key values that trigger focus ring logic
const NAVIGATION_KEYS = [
  ' ',
  'F2',
  'Enter',
  'Esc', // Firefox 36 and earlier uses 'Esc' instead of 'Escape'
  'Escape',
  'ArrowLeft',
  'ArrowUp',
  'ArrowRight',
  'ArrowDown',
  'Home',
  'End',
  'PageUp',
  'PageDown'
];

// constant event.key values that trigger 'scroll to current' logic
const SCROLL_KEYS = [
  ' ',
  'F2',
  'Enter',
  'Esc', // Firefox 36 and earlier uses 'Esc' instead of 'Escape'
  'Escape'
];

/**
 * Hook that manages focus interactions on the Preact List
 */
export function useFocusHandling<K extends string | number, D>({
  rootRef,
  viewportInfo,
  listId,
  listData,
  hasMore,
  selectionExtensionElementRef,
  currentItemOverride,
  onPersistCurrentItem,
  isEmbedded,
  itemEnterKeyFocusBehavior,
  isDataLoaded,
  reorderContext,
  deferredScrollDetail,
  deferredScrollDetailRef,
  updateDeferredScrollDetail
}: UseFocusHandlingProps<K, D>) {
  // tracks whether the most recent 'blur' was caused by focus being lost by the browser window (or iframe)
  // used when determining whether scrolling active into view should be skipped when focus is regained
  const blurFromWindowRef = useRef<boolean>(false);

  // tracks whether a keydown 'tab' is currently detected - used when handling a keyup 'tab' event to scroll
  // the active cell into view in the event that focus is received from outside of the page (or iframe)
  const isTabDownDetectedRef = useRef<boolean>(false);

  // tracks focus loss during render cycle for potential focus recapture cases
  const isRenderCycle = useRef<boolean>(true);
  isRenderCycle.current = true;
  const blurredRenderCycleElementRef = useRef<FocusableElement | null>(null);

  // track current 'focus info' state
  const hasFocusRef = useRef<boolean>(false);
  const [isTabbableMode, setIsTabbableMode] = useState<boolean>(false);
  const [isShowFocusRing, setIsShowFocusRing] = useState<boolean>(false);

  // track pointer down information
  const recentPointerRef = useRef<boolean>(false);
  const pointerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isShowFocusRingOverrideRef = useRef<boolean | null>(null);

  // track the active and current cell information
  const [activeCell, setActiveCell] = useState<ActiveCell<K> | undefined>(undefined);
  const currencyStatusRef = useRef<CurrencyStatus<K, D>>({
    hasPendingCurrentCellOverride: false,
    hasPendingCurrentCellOverrideScroll: false
  });

  const wasTransient = isTransientCell(currencyStatusRef.current.activeCell);
  const currentActiveCell = determineActiveCell<K, D>(
    currencyStatusRef,
    listData,
    hasMore,
    isEmbedded,
    currentItemOverride
  );

  // intercept onPersistCurrentItem calls to ensure internal state is updated appropriately
  const onActiveCellChanged = useMemo(() => {
    return (
      detail: ActiveCellDetail<K>,
      isAutoFocus?: boolean,
      isSelectionExtension?: boolean,
      skipScrollCellIntoView?: boolean
    ) => {
      const newActiveCell = detail.value;
      if (!cellComparator(currencyStatusRef.current.activeCell, newActiveCell)) {
        setActiveCell(newActiveCell);
        currencyStatusRef.current.activeCell = newActiveCell;
        if (!isTransientCell(newActiveCell)) {
          const newCurrentCell = newActiveCell as Cell<K>;
          // only update current cell when 'active' is not one of the transient 'skeleton' regions
          if (onPersistCurrentItem != null && newCurrentCell.rowKey != null) {
            onPersistCurrentItem({ value: newCurrentCell.rowKey });
          }
          currencyStatusRef.current.currentCell = newCurrentCell;

          // make sure pending current cell override states are updated on current changed
          if (newCurrentCell != null) {
            currencyStatusRef.current.previousEmbeddedSelectCurrentCell = newCurrentCell;
          }
          currencyStatusRef.current.currentItemOverride = currentItemOverride;
          currencyStatusRef.current.hasPendingCurrentCellOverride = false;
          currencyStatusRef.current.hasPendingCurrentCellOverrideScroll = false;
        } else if (newActiveCell?.type === 'loadMore') {
          currencyStatusRef.current.loadMoreInitialData = listData;
        }
        if (!isSelectionExtension) {
          selectionExtensionElementRef.current = undefined;
          if (isAutoFocus && newActiveCell != null) {
            // attempt to auto-focus cell contents
            setIsTabbableMode(true);
            setIsShowFocusRing(false);
          } else if (isTabbableMode) {
            // exit tabbable mode when navigating away from auto-focus cell
            setIsTabbableMode(false);
          }
        }
      }
      if (newActiveCell != null && !recentPointerRef.current && !skipScrollCellIntoView) {
        updateDeferredScrollDetail({
          cell: newActiveCell,
          locationX: 'inView',
          locationY: 'inView'
        });
      }
    };
  }, [
    currentItemOverride,
    isTabbableMode,
    listData,
    onPersistCurrentItem,
    selectionExtensionElementRef,
    updateDeferredScrollDetail
  ]);

  // Helper function to set the List's focus tracking state to reflect that it is inactive.
  const _setAsInactive = useCallback(() => {
    hasFocusRef.current = false;

    setIsTabbableMode(false);
    setIsShowFocusRing(false);
  }, []);

  // Helper function to set the List's focus tracking state to reflect that one of its cells has focus.
  const _enableNavigationMode = useCallback(
    (cell: ActiveCell<K>, skipScrollCellIntoView?: boolean) => {
      const rootElement = rootRef.current;
      const scrollerElement = viewportInfo.viewportConfig.scrollerRef.current;
      if (rootElement != null && scrollerElement != null) {
        setIsTabbableMode(false);
        const focusRingState =
          isShowFocusRingOverrideRef.current !== null
            ? isShowFocusRingOverrideRef.current
            : !recentPointerRef.current;
        setIsShowFocusRing(focusRingState);
        onActiveCellChanged({ value: cell }, false, false, skipScrollCellIntoView);
      }
    },
    [rootRef, viewportInfo, onActiveCellChanged]
  );

  // Helper function to set the List's focus tracking state to reflect that it is tabbable.
  const _enableTabbableMode = useCallback(
    (cell: ActiveCell<K>, isKeyboard?: boolean, skipScrollCellIntoView?: boolean) => {
      const rootElement = rootRef.current;
      const scrollerElement = viewportInfo.viewportConfig.scrollerRef.current;
      if (rootElement != null && scrollerElement != null) {
        setIsTabbableMode(true);
        if (!isKeyboard) {
          setIsShowFocusRing(false);
          onActiveCellChanged({ value: cell }, true, false, skipScrollCellIntoView);
        }
      }
    },
    [rootRef, viewportInfo, onActiveCellChanged]
  );

  // Helper function to set the List's focus tracking state to reflect that a user has
  // exited tabbable mode via the keyboard (esc or 'F2')
  const _disableTabbableMode = useCallback(
    (cell: ActiveCell<K>) => {
      const rootElement = rootRef.current;
      if (rootElement != null) {
        setIsTabbableMode(false);
        const cellElement = getElementFromCell(rootElement, listId, cell);
        if (cellElement != null) {
          cellElement.focus({ preventScroll: true });
        } else {
          rootElement.focus({ preventScroll: true });
        }
      }
    },
    [rootRef, listId]
  );

  const notifyFocusFromPointer = useCallback((isTouch: boolean) => {
    if (pointerTimerRef.current != null) {
      clearTimeout(pointerTimerRef.current);
    }
    recentPointerRef.current = true;
    pointerTimerRef.current = setTimeout(
      () => {
        recentPointerRef.current = false;
      },
      isTouch ? TOUCH_FOCUS_SHIFT_TIMEOUT : MOUSE_FOCUS_SHIFT_TIMEOUT
    );
  }, []);

  // PointerDown handler for the outer List. Focus transfers that occur following a pointerdown
  // on desktop devices should not result in a focus ring being shown on the focused element
  const _pointerDownHandler = useCallback(
    (event: PointerEvent) => {
      if (isShowFocusRing) {
        setIsShowFocusRing(false);
      }
      if (event.pointerType !== 'touch') {
        notifyFocusFromPointer(false);
      }
    },
    [isShowFocusRing, notifyFocusFromPointer]
  );

  // PointerUp handler for the outer List. Focus transfers that occur following a pointerup
  // on any type of device should not result in a focus ring being shown on the focused element
  const _pointerUpHandler = useCallback(
    (event: PointerEvent) => {
      notifyFocusFromPointer(event.pointerType === 'touch');
    },
    [notifyFocusFromPointer]
  );

  // Focus handler for the outer List. On focus, a focusable area needs to be 'active'.
  // If an 'active' area already exists, nothing further is needed. Otherwise, the first
  // focusable element should be made 'active'.
  const _onFocusHandler = (event: FocusEvent) => {
    hasFocusRef.current = true;
    const rootElement = rootRef.current;
    const scrollerElement = viewportInfo.viewportConfig.scrollerRef.current;
    if (rootElement != null) {
      // don't scroll active into view if previous blur was due to window focus loss
      const skipScrollCellIntoView = blurFromWindowRef.current;
      blurFromWindowRef.current = false;

      const targetElement = event.target as HTMLElement;
      if (targetElement === rootElement || targetElement === scrollerElement) {
        if (currentActiveCell != null) {
          _enableNavigationMode(currentActiveCell, skipScrollCellIntoView);
        }
      } else {
        const cell = logicalCellExtractor<K>(targetElement, listId);
        if (cell != null) {
          if (getCellType(targetElement) != null) {
            // handle individual focusable regions receiving focus
            _enableNavigationMode(cell, skipScrollCellIntoView);
          } else {
            _enableTabbableMode(cell, false, skipScrollCellIntoView);
          }
        }
      }
    }
    isShowFocusRingOverrideRef.current = null;
  };

  // Blur handler for the outer List. When a blur is caused by the browser window itself
  // losing focus (changing tabs or leaving an iframe for example), an eventual re-focus due
  // to the window re-gaining focus needs to be handled differently (no auto-scroll mostly).
  const _onBlurHandler = useCallback(
    (event: FocusEvent) => {
      const rootElement = rootRef.current;
      if (rootElement != null) {
        if (event.target === document.activeElement) {
          // save focus ring state so it's set once focus comes back
          isShowFocusRingOverrideRef.current = isShowFocusRing;
        }
        const relatedTarget = event.relatedTarget as Element;
        const isUnknownRelatedTarget = relatedTarget == null;
        blurFromWindowRef.current = isUnknownRelatedTarget;
        if (isUnknownRelatedTarget || !getElementContainsFunc(rootElement, true)(relatedTarget)) {
          _setAsInactive();
          if (isRenderCycle.current) {
            // store element that loses focus during a render cycle for recapturing focus logic
            blurredRenderCycleElementRef.current = event.target as FocusableElement;
          }
          // reset tab down detection when component loses focus
          isTabDownDetectedRef.current = false;
        }
      }
    },
    [rootRef, isShowFocusRing, _setAsInactive]
  );

  // KeyDown handler for the outer List. This specific handler tracks 'focus' transfer keys
  // Esc, F2, Enter, and Tab. It also ensures the active cell is scrolled into view on any key
  const _onKeyDownHandler = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        isTabDownDetectedRef.current = true;
      }
      const rootElement = rootRef.current;
      const scrollerElement = viewportInfo.viewportConfig.scrollerRef.current;
      if (rootElement != null && scrollerElement != null) {
        const isNavigationKey = NAVIGATION_KEYS.indexOf(event.key) > -1;
        if (isNavigationKey) {
          if (pointerTimerRef.current != null) {
            clearTimeout(pointerTimerRef.current);
          }
          pointerTimerRef.current = null;
          recentPointerRef.current = false;
        }
        if (activeCell != null) {
          if (event.key === 'F2') {
            if (isTabbableMode) {
              _disableTabbableMode(activeCell);
            } else {
              _enableTabbableMode(activeCell, true);
            }
          } else if (event.key === 'Esc' || event.key === 'Escape') {
            // Firefox 36 and earlier uses 'Esc' instead of 'Escape'
            _disableTabbableMode(activeCell);
          } else if (
            event.key === 'Enter' &&
            !isTabbableMode &&
            !isEmbedded &&
            itemEnterKeyFocusBehavior === 'focusWithin'
          ) {
            _enableTabbableMode(activeCell, true);
          }
          if (!isTabbableMode) {
            if (isNavigationKey) {
              setIsShowFocusRing(true);
            }
            if (SCROLL_KEYS.indexOf(event.key) > -1) {
              updateDeferredScrollDetail({
                cell: activeCell,
                locationX: 'inView',
                locationY: 'inView'
              });
            }
          }
        }
      }
    },
    [
      rootRef,
      viewportInfo,
      isTabbableMode,
      isEmbedded,
      activeCell,
      _enableTabbableMode,
      _disableTabbableMode,
      updateDeferredScrollDetail,
      itemEnterKeyFocusBehavior
    ]
  );

  // KeyUp handler for the outer Table. This specific handler tracks the 'tab' key
  // It ensures the active cell is scrolled into view when tabbing into the component
  const _onKeyUpHandler = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        // if a keyup 'tab' event is detected without a matching keydown 'tab', scroll active
        // cell into view to handle case where focus is gained from outside the page (or iframe)
        if (activeCell != null && !isTabDownDetectedRef.current) {
          updateDeferredScrollDetail({
            cell: activeCell,
            locationX: 'inView',
            locationY: 'inView'
          });
        }
        isTabDownDetectedRef.current = false;
      }
    },
    [activeCell, updateDeferredScrollDetail]
  );

  // Callback for the outer List's FocusTracker usage.
  const onStartFocusTracking = useCallback(
    (detail: {
      activeElement: FocusableElement;
      hasFocusWithin: boolean;
      tabbableElements: FocusableElement[];
    }) => {
      const rootElement = rootRef.current;
      if (rootElement != null && activeCell != null) {
        handleEnterActionableMode(
          detail.hasFocusWithin && getCellType(detail.activeElement) == null,
          allTabbableElements(
            getElementFromCell(rootElement, listId, activeCell) as FocusableElement
          ),
          (value?: ActiveCell<K>) => {
            setIsTabbableMode(value != undefined);
          }
        );
      }
    },
    [rootRef, activeCell, listId]
  );

  // Callback for the outer List's FocusTracker usage.
  const onFocusStartEdge = useCallback((detail: FocusGuardCallbackDetail) => {
    handleWrapActionableFocus(true, detail.tabbableElements, (value?: ActiveCell<K>) => {
      setIsTabbableMode(value != undefined);
    });
  }, []);

  // Callback for the outer List's FocusTracker usage.
  const onFocusEndEdge = useCallback((detail: FocusGuardCallbackDetail) => {
    handleWrapActionableFocus(false, detail.tabbableElements, (value?: ActiveCell<K>) => {
      setIsTabbableMode(value != undefined);
    });
  }, []);

  // Ensure focus is properly set at the completion of every render cycle
  // NO DEPENDENCY ARRAY HERE TO ENSURE THIS RUNS ON EVERY RENDER CYCLE
  useEffect(() => {
    // make sure the reorder currentItemKey is always up to date
    if (reorderContext != null) {
      reorderContext.currentItemKey.current = activeCell?.rowKey;
    }

    const rootElement = rootRef.current;
    const scrollerElement = viewportInfo.viewportConfig.scrollerRef.current;
    if (rootElement != null && scrollerElement != null) {
      // internal method responsible for ensuring focus is applied to the appropriate cells or contents
      const _applyInternalFocus = (
        rootElement: HTMLDivElement,
        activeElement: FocusableElement,
        isCaptureFocus?: boolean
      ) => {
        if (currentActiveCell != null) {
          // ensure real browser focus is on the correct cell element
          const cellElement = getElementFromCell(rootElement, listId, currentActiveCell);
          if (cellElement != null) {
            if (isCaptureFocus) {
              applyScrollDetail(rootElement, scrollerElement, viewportInfo, listId, {
                cell: currentActiveCell,
                locationX: 'inView',
                locationY: 'inView'
              });
            }
            // cell is valid, so set focus on it if needed
            if (!isTabbableMode && activeElement !== cellElement) {
              if (
                wasTransient &&
                !isTransientCell(currentActiveCell) &&
                isAutoFocusCell(rootElement, listId, currentActiveCell)
              ) {
                _enableTabbableMode(currentActiveCell);
              } else {
                cellElement.focus({ preventScroll: true });
              }
              return;
            } else if (
              isTabbableMode &&
              !getElementContainsFunc(cellElement, true)(activeElement)
            ) {
              if (isCaptureFocus) {
                _disableTabbableMode(currentActiveCell);
              } else {
                handleEnterActionableMode(
                  false,
                  allTabbableElements(cellElement),
                  (value?: ActiveCell<K>) => {
                    setIsTabbableMode(value != undefined);
                    if (isCaptureFocus) {
                      rootElement.focus({ preventScroll: true });
                    }
                  },
                  true
                );
              }
              return;
            } else if (activeCell !== currentActiveCell) {
              // if element is already focused, ensure state is up to date
              // onActiveCellChanged({ value: currentActiveCell }, false, false, true);
            }
          }
        }
        if (isCaptureFocus) {
          rootElement.focus({ preventScroll: true });
        }
      };

      // ensure the current cell is scrolled into view when necessary
      // only run if the state and ref match - otherwise another render cycle may be pending
      if (
        deferredScrollDetail != null &&
        deferredScrollDetail === deferredScrollDetailRef.current
      ) {
        applyScrollDetail(rootElement, scrollerElement, viewportInfo, listId, deferredScrollDetail);
        deferredScrollDetailRef.current = undefined;
      }

      // ensure current-cell-override is up to date on each render
      applyCurrentCellOverride(
        rootElement,
        scrollerElement,
        viewportInfo,
        listId,
        currencyStatusRef,
        onActiveCellChanged,
        isEmbedded,
        isDataLoaded,
        currentItemOverride
      );

      const blurredRenderCycleElement = blurredRenderCycleElementRef.current;
      blurredRenderCycleElementRef.current = null;
      isRenderCycle.current = false;

      const activeElement = getActiveElement(rootElement);
      // if we were tracking focus during the render cycle, ensure we recapture focus if needed
      if (
        blurredRenderCycleElement != null &&
        activeElement === getBodyElement(rootElement) &&
        (!getElementContainsFunc(rootElement, true)(blurredRenderCycleElement) ||
          !isTabbableElement(blurredRenderCycleElement, true))
      ) {
        deferredScrollDetailRef.current = undefined;
        _applyInternalFocus(rootElement, activeElement, true);
      } else if (
        hasFocusRef.current &&
        cellComparator(activeCell, currencyStatusRef.current.activeCell)
      ) {
        _applyInternalFocus(rootElement, activeElement);
      }
    }
  });

  return {
    focusHandlingProps: {
      onPointerDown: _pointerDownHandler,
      onPointerUp: _pointerUpHandler,
      onFocus: _onFocusHandler,
      onBlur: _onBlurHandler,
      onKeyDown: _onKeyDownHandler,
      onKeyUp: _onKeyUpHandler
    },
    activeCell,
    onActiveCellChanged,
    currentCell: currencyStatusRef.current.currentCell,
    currentActiveCell,
    isShowFocusRing,
    isTabbableMode,
    onStartFocusTracking,
    onFocusStartEdge,
    onFocusEndEdge,
    updateDeferredScrollDetail
  };
}
